import React, { useRef, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { UncontrolledTooltip } from 'reactstrap';

import Accordion from '~components/Accordion';
import DraggableItem from '~components/drag-and-drop/DraggableItem';
import Icon, {
  IconProp,
  faAlignLeft,
  faCalendar,
  faCheck,
  faChevronDown,
  faCircleDot,
  faCopy,
  faFont,
  faGripLines,
  faPenNib,
  faSignature,
  faSquareCheck,
} from '~components/icon';

import { useToast } from '~libs/toast';

import { makeDefaultField } from '~forms/lib/fieldFactory';
import { addField } from '~forms/reducers/formBuilderSlice';
import { FormFieldType, MergeFieldRegistryEntry } from '~forms/types';

interface FieldTypeDef {
  type: FormFieldType;
  label: string;
  icon: IconProp;
  paletteId: number;
}

const FIELD_TYPE_DEFS: FieldTypeDef[] = [
  { type: 'single_line_text', label: 'Single Line', icon: faFont, paletteId: 1 },
  { type: 'paragraph_text', label: 'Paragraph', icon: faAlignLeft, paletteId: 2 },
  { type: 'date', label: 'Date', icon: faCalendar, paletteId: 3 },
  { type: 'checkbox', label: 'Checkbox', icon: faSquareCheck, paletteId: 4 },
  { type: 'multiple_choice', label: 'Multi-choice', icon: faCircleDot, paletteId: 5 },
  { type: 'dropdown', label: 'Dropdown', icon: faChevronDown, paletteId: 6 },
  { type: 'signature', label: 'Signature', icon: faSignature, paletteId: 7 },
  { type: 'section_divider', label: 'Section', icon: faGripLines, paletteId: 8 },
  { type: 'rich_text', label: 'Rich Text', icon: faPenNib, paletteId: 9 },
];

const COPIED_RESET_MS = 1500;

// One merge-field row: click anywhere on the row (or the icon) to copy the {{merge:<token>}} text.
// The trailing ClickableIcon is the keyboard-accessible control and flips to a tick once copied.
interface MergeFieldRowProps {
  entry: MergeFieldRegistryEntry;
  onCopy: (entry: MergeFieldRegistryEntry) => Promise<boolean>;
}

const MergeFieldRow: React.FC<MergeFieldRowProps> = ({ entry, onCopy }) => {
  const [copied, setCopied] = useState(false);
  // Guards against rapid repeat triggers while the copied confirmation is showing.
  const busyRef = useRef(false);

  const doCopy = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    const ok = await onCopy(entry);
    if (ok) setCopied(true);
    window.setTimeout(() => {
      setCopied(false);
      busyRef.current = false;
    }, COPIED_RESET_MS);
  };

  // The whole row is a single keyboard-accessible button (matching the palette tiles above). The
  // copy/tick icon is decorative — making it a second focusable control would nest interactive
  // elements (axe: nested-interactive).
  return (
    <div
      className="merge-field-entry"
      onClick={doCopy}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          doCopy();
        }
      }}
      title={`Copy "${entry.label}" — paste it into a field's prefill`}
      aria-label={copied ? `Copied ${entry.label}` : `Copy merge field: ${entry.label}`}
    >
      <span className="merge-field-label">{entry.label}</span>
      <Icon
        icon={copied ? faCheck : faCopy}
        className={`merge-field-copy-icon${copied ? ' text-success' : ''}`}
        aria-hidden
      />
    </div>
  );
};

interface MergeGroupBodyProps {
  entries: MergeFieldRegistryEntry[];
  onCopy: (entry: MergeFieldRegistryEntry) => Promise<boolean>;
}

const MergeGroupBody: React.FC<MergeGroupBodyProps> = ({ entries, onCopy }) => (
  <div>
    {entries.map((entry) => (
      <MergeFieldRow key={entry.id} entry={entry} onCopy={onCopy} />
    ))}
  </div>
);

interface ComponentProps {
  mergeFieldGroups: Record<string, MergeFieldRegistryEntry[]>;
  readOnly?: boolean;
}

const FieldPalette: React.FC<ComponentProps> = ({ mergeFieldGroups, readOnly = false }) => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();

  const handleClickAdd = (def: FieldTypeDef) => {
    if (readOnly) return;
    dispatch(addField(makeDefaultField(def.type)));
  };

  // Copy the merge token to the clipboard so the user can paste it into a field's prefill.
  // Returns whether the copy succeeded so the row can show its tick confirmation.
  const handleCopy = async (entry: MergeFieldRegistryEntry): Promise<boolean> => {
    const sentinel = `{{merge:${entry.token}}}`;
    try {
      await navigator.clipboard.writeText(sentinel);
      return true;
    } catch {
      showToast('danger', 'Could not copy to the clipboard');
      return false;
    }
  };

  const mergeAccordionItems = Object.entries(mergeFieldGroups).map(([groupName, entries]) => ({
    header: groupName,
    bodyComponent: MergeGroupBody,
    bodyProps: { entries, onCopy: handleCopy },
  }));

  return (
    <div className="field-palette">
      <div className="field-palette-section-title">Field Types</div>
      <div className="field-palette-tiles">
        {FIELD_TYPE_DEFS.map((def) => (
          <DraggableItem
            key={def.type}
            id={def.paletteId}
            disabled={readOnly}
            data={{
              type: 'palette-field',
              fieldType: def.type,
              children: (
                <div className="field-palette-tile">
                  <span className="field-palette-tile-icon">
                    <Icon icon={def.icon} />
                  </span>
                  <span>{def.label}</span>
                </div>
              ),
            }}
          >
            <div
              className={`field-palette-tile${readOnly ? ' drag-disabled' : ''}`}
              onClick={() => handleClickAdd(def)}
              role="button"
              tabIndex={readOnly ? -1 : 0}
              aria-disabled={readOnly}
              onKeyDown={(e) => {
                if (readOnly) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClickAdd(def);
                }
              }}
              aria-label={`Add ${def.label} field`}
            >
              <span className="field-palette-tile-icon">
                <Icon icon={def.icon} />
              </span>
              <span>{def.label}</span>
            </div>
          </DraggableItem>
        ))}
      </div>

      {mergeAccordionItems.length > 0 && (
        <>
          <div className="field-palette-section-title mt-3 d-flex align-items-center">
            <span>Merge Fields</span>
            <span id="merge-fields-help" className="merge-fields-help" role="img" aria-label="Merge field help">
              ?
            </span>
            <UncontrolledTooltip target="merge-fields-help" placement="right">
              <div className="text-start merge-fields-help-tooltip">
                <div>Click a merge field to copy it, then paste it into a field&apos;s prefill.</div>
                <div className="mt-1">
                  <code>Example: {'{{merge:client.firstName}}'}</code>
                </div>
                <div>
                  <code>Returns: John</code>
                </div>
              </div>
            </UncontrolledTooltip>
          </div>
          <Accordion items={mergeAccordionItems} allowMultiple />
        </>
      )}
    </div>
  );
};

export default FieldPalette;
